import { useNavigate } from '@tanstack/react-router';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { Button, DropDownButton, DropdownItem, IconName } from '@optimus-web/core';
import { EnterpriseLinkIconButton, GuideLinkButton, TooltippedIconButton } from '@mews-commander/ui';
import { ArticlePath, createGuideLink, FeatureFlag } from '@mews-commander/enums';
import { lt, useFeatureFlag } from '@mews/framework';
import { ServiceType, useInvalidateRepositoryQuery } from '@mews-commander/data-access';
import { useCurrentService } from '../queries/useCurrentService';
import { useProductDialogForm } from '../forms/useCreateProductDialogForm';
import { useAllowanceDialogForm } from '../forms/useCreateAllowanceDialogForm';
import { useProductCategoryDialogForm } from '../forms/useCreateProductCategoryDialogForm';

export const useProductListActions = () => {
    const [renderAllowanceDialog, toggleAllowanceDialog] = useAllowanceDialogForm();
    const [renderProductDialog, toggleProductDialog] = useProductDialogForm();
    const [renderProductCategoryDialog, toggleProductCategoryDialog] = useProductCategoryDialogForm();

    const currentEnterpriseId = useCurrentEnterpriseId();
    const { id, data } = useCurrentService();

    const navigate = useNavigate();
    const invalidate = useInvalidateRepositoryQuery();

    const areAllowancesEnabled = useFeatureFlag(FeatureFlag.allowancesEnabled);
    const isProductCategoryFormEnabled = useFeatureFlag(FeatureFlag.fePoductCategoryFormEnabled);

    const handleProductCreateClick =
        data.discriminator === ServiceType.Bookable
            ? toggleProductDialog
            : async () =>
                  navigate({
                      to: '/$currentEnterpriseId/Product/Create',
                      params: {
                          currentEnterpriseId,
                      },
                      search: {
                          OwnerId: id,
                      },
                  });

    const actions = (
        <>
            {areAllowancesEnabled ? (
                <DropDownButton icon={IconName.Add} label={lt('Create')} size="medium">
                    <DropdownItem label={lt('Product')} value="product" onClick={handleProductCreateClick} />
                    <DropdownItem onClick={toggleAllowanceDialog} label={lt('Allowance')} value="allowance" />
                </DropDownButton>
            ) : (
                <Button type="button" onClick={handleProductCreateClick} icon={IconName.Add} size="medium">
                    {lt('Create')}
                </Button>
            )}
            {isProductCategoryFormEnabled ? (
                <TooltippedIconButton
                    icon={IconName.FolderOpened}
                    onClick={toggleProductCategoryDialog}
                    tooltip={lt('ProductCategory')}
                    type="button"
                />
            ) : (
                <EnterpriseLinkIconButton
                    icon={IconName.FolderOpened}
                    to="ProductCategory/Create"
                    search={{
                        OwnerId: id,
                    }}
                    onClick={async () => invalidate('products', 'getOverviewEntries')}
                    tooltip={lt('ProductCategory')}
                    type="button"
                />
            )}
            <EnterpriseLinkIconButton
                icon={IconName.Edit}
                to="Product/BatchTaxRateUpdate"
                search={{
                    OwnerId: id,
                }}
                onClick={async () => invalidate('products', 'getOverviewEntries')}
                type="button"
                tooltip={lt('BatchProductTaxRateUpdate')}
            />
            <GuideLinkButton guideLink={createGuideLink(ArticlePath.UnderstandingServices)} />
        </>
    );

    return {
        actions,
        renderAllowanceDialog,
        renderProductDialog,
        renderProductCategoryDialog,
    };
};
